import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, FormLabel, Grid, Input } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EnvelopeSimple } from 'phosphor-react';
import { useNfseEmissao } from '../../../../../../hooks/useNfseEmissao';
import { INfseEmissao } from '../../../../../../types/nfseEmissao';
import { extractApiErrorMessage } from '../../../../../../utils/ErrorUtils';
import { island } from '../../shared/NfseIsland';
import { Banner, C, dataBR, horaBR, NButton } from '../../shared/ui';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const lista = (v: string) =>
  v
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);

export function EmailForm({ emissao }: { emissao: INfseEmissao }) {
  const queryClient = useQueryClient();
  const { enviarEmail } = useNfseEmissao();
  const [para, setPara] = useState(emissao.tomador_email ?? '');
  const [copia, setCopia] = useState('');
  const [reenviar, setReenviar] = useState(false);

  useEffect(() => setPara(emissao.tomador_email ?? ''), [emissao.tomador_email]);

  const mutation = useMutation({
    mutationFn: () => enviarEmail(emissao.id, para.trim(), copia.trim()),
    onSuccess: () => {
      island.notify(`Nota enviada para ${para.trim()}`, { tone: 'ok', meta: 'PDF + XML' });
      setReenviar(false);
      queryClient.invalidateQueries({ queryKey: ['nfse-emissao', emissao.id] });
    },
    onError: err =>
      island.notify(extractApiErrorMessage(err, 'Não foi possível enviar o e-mail.'), {
        tone: 'bad',
      }),
  });

  if (emissao.email_enviado_em && !reenviar) {
    return (
      <Banner
        tone="success"
        compact
        action={
          <NButton size="sm" variant="ghost" onClick={() => setReenviar(true)}>
            Enviar de novo
          </NButton>
        }
      >
        Enviado em {dataBR(emissao.email_enviado_em)} às {horaBR(emissao.email_enviado_em)} para{' '}
        <b>{emissao.email_enviado_para}</b>. O envio ficou registrado na nota.
      </Banner>
    );
  }

  const valido = EMAIL_RE.test(para.trim()) && lista(copia).every(e => EMAIL_RE.test(e));
  const estilo = {
    borderRadius: '10px',
    bg: 'rgba(255, 255, 255, 0.78)',
    borderColor: C.lineInput,
  };

  return (
    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr auto' }} gap={3} alignItems="end">
      <FormControl isRequired>
        <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1} htmlFor="mail-to">
          Para
        </FormLabel>
        <Input
          id="mail-to"
          type="email"
          value={para}
          onChange={e => setPara(e.target.value)}
          {...estilo}
        />
        <FormHelperText fontSize="11.5px" color={C.subtle}>
          Veio do cadastro do tomador
        </FormHelperText>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1} htmlFor="mail-cc">
          Cópia (opcional)
        </FormLabel>
        <Input
          id="mail-cc"
          value={copia}
          onChange={e => setCopia(e.target.value)}
          placeholder="contabil@cliente.com.br"
          {...estilo}
        />
        <FormHelperText fontSize="11.5px" color={C.subtle}>
          Separe por vírgula
        </FormHelperText>
      </FormControl>
      <NButton
        icon={EnvelopeSimple}
        mb="22px"
        isDisabled={!valido}
        isLoading={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        Enviar
      </NButton>
    </Grid>
  );
}
